'use client'

import React from 'react'
import Link from 'next/link'
import { ArrowUpRight, ExternalLink } from 'lucide-react'
import type { SectionButton } from '@/lib/persona-sections'
import { trackCtaClicked } from '../../lib/analytics'

interface PersonaSectionButtonProps {
  button: SectionButton
  /** Extra classes for placement; the button's own styling stays fixed. */
  className?: string
}

/**
 * The CMS-configured button for a "How We Help" section. Rendered in both the
 * section banner and the section content block, so the two always look and
 * behave the same.
 */
export function PersonaSectionButton({ button, className = '' }: PersonaSectionButtonProps) {
  const { label, href, isExternal } = button

  const classes = `inline-flex items-center gap-2 px-6 py-3 bg-brand-blue text-white rounded-full font-medium text-sm hover:bg-brand-blue/90 active:bg-brand-blue/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold transition-colors ${className}`

  const track = () =>
    trackCtaClicked({
      cta_text: label,
      cta_location: 'mid_page',
      destination_url: href,
    })

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${label} (opens in a new tab)`}
        onClick={track}
        className={classes}
      >
        {label}
        <ExternalLink className="w-4 h-4" aria-hidden="true" />
      </a>
    )
  }

  return (
    <Link href={href} onClick={track} className={classes}>
      {label}
      <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
    </Link>
  )
}
